import { useState, useMemo, useCallback, useEffect } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Checkbox,
  Input,
  Label,
} from '@pantry/ui';
import { PackageIcon, CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import {
  useTripItems,
  useStorageLocations,
  useCompleteTrip,
  type StorageLocation,
} from '@pantry/api-client';
import type { ShoppingTripItem } from '@pantry/shared';

interface TripCompletionDialogProps {
  householdId: string;
  listId: string;
  tripId: string;
  listName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: () => void;
}

interface TripItemSelection {
  selected: boolean;
  storageLocationId: string;
  expiryDays: number | null;
  quantity: number;
}

const EXPIRY_PRESETS = [
  { label: '+3 days', days: 3 },
  { label: '+1 week', days: 7 },
  { label: '+2 weeks', days: 14 },
  { label: '+1 month', days: 30 },
  { label: '+3 months', days: 90 },
  { label: '+6 months', days: 180 },
] as const;

/**
 * Trip completion dialog — shows all scanned items, lets user select which
 * to add to inventory with per-item storage location, expiry, and quantity.
 *
 * On-list items are pre-selected. Off-list items are opt-in.
 */
export function TripCompletionDialog({
  householdId,
  listId,
  tripId,
  listName,
  open,
  onOpenChange,
  onComplete,
}: TripCompletionDialogProps) {
  const { t } = useLingui();
  const { data: tripItems } = useTripItems(householdId, listId, tripId);
  const { data: storageLocations } = useStorageLocations(householdId);
  const completeTrip = useCompleteTrip();

  const [defaultStorageId, setDefaultStorageId] = useState('');
  const [selections, setSelections] = useState<Map<string, TripItemSelection>>(new Map());

  // Split items into on-list and off-list
  const onListItems = useMemo(() => (tripItems ?? []).filter((i) => i.isOnList), [tripItems]);
  const offListItems = useMemo(() => (tripItems ?? []).filter((i) => !i.isOnList), [tripItems]);

  useEffect(() => {
    if (!tripItems?.length) return;
    setSelections((prev) => {
      const next = new Map(prev);
      for (const item of [...(onListItems ?? []), ...(offListItems ?? [])]) {
        if (!next.has(item.id)) {
          next.set(item.id, {
            selected: item.isOnList ?? false,
            storageLocationId: '',
            expiryDays: null,
            quantity: 1,
          });
        }
      }
      return next;
    });
  }, [tripItems]);

  // Initialize selections when trip items load
  const getSelection = useCallback(
    (itemId: string, isOnList: boolean): TripItemSelection => {
      const stored = selections.get(itemId);
      if (stored) {
        // Resolve storageLocationId at read-time: fall back to defaultStorageId
        // for items that haven't been explicitly assigned a location
        return {
          ...stored,
          storageLocationId: stored.storageLocationId || defaultStorageId,
        };
      }
      return {
        selected: isOnList, // On-list pre-selected, off-list opt-in
        storageLocationId: defaultStorageId,
        expiryDays: null,
        quantity: 1,
      };
    },
    [selections, defaultStorageId]
  );

  const updateSelection = (itemId: string, updates: Partial<TripItemSelection>) => {
    setSelections((prev) => {
      const next = new Map(prev);
      const existing = next.get(itemId) ?? {
        selected: true,
        storageLocationId: defaultStorageId,
        expiryDays: null,
        quantity: 1,
      };
      next.set(itemId, { ...existing, ...updates });
      return next;
    });
  };

  const selectedCount = useMemo(() => {
    let count = 0;
    for (const item of [...onListItems, ...offListItems]) {
      const sel = getSelection(item.id, item.isOnList ?? false);
      if (sel.selected) count++;
    }
    return count;
  }, [onListItems, offListItems, getSelection]);

  const handleComplete = async () => {
    const allItems = [...onListItems, ...offListItems];
    const items = allItems.map((item) => {
      const sel = getSelection(item.id, item.isOnList ?? false);

      const expiryDate =
        sel.selected && sel.expiryDays
          ? new Date(Date.now() + sel.expiryDays * 24 * 60 * 60 * 1000).toISOString()
          : undefined;

      return {
        tripItemId: item.id,
        addToInventory: sel.selected,
        storageLocationId: sel.selected ? sel.storageLocationId || undefined : undefined,
        expiryDate,
        quantity: sel.quantity,
      };
    });

    const addedCount = items.filter((i) => i.addToInventory).length;

    try {
      await completeTrip.mutateAsync({
        householdId,
        listId,
        tripId,
        data: { items },
      });
      toast.success(t`Trip completed! ${addedCount} items added to inventory.`);
      onOpenChange(false);
      onComplete?.();
    } catch {
      toast.error(t`Failed to complete trip. Please try again.`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            <Trans>Trip Summary</Trans>
          </DialogTitle>
          <DialogDescription>
            {listName} · {new Date().toLocaleDateString()}
          </DialogDescription>
        </DialogHeader>

        {/* Default storage location */}
        <div className="flex items-center gap-3 py-3">
          <Label className="text-sm text-muted-foreground shrink-0">
            <Trans>Default storage:</Trans>
          </Label>
          <select
            value={defaultStorageId}
            onChange={(e) => setDefaultStorageId(e.target.value)}
            className="h-9 w-48 rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="">{t`Select location`}</option>
            {(storageLocations ?? []).map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </select>
        </div>

        {/* On-list items section */}
        {onListItems.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              <Trans>On Your List</Trans> ({onListItems.length})
            </p>
            <div className="space-y-2">
              {onListItems.map((item) => (
                <CompletionItemRow
                  key={item.id}
                  item={item}
                  selection={getSelection(item.id, true)}
                  storageLocations={storageLocations ?? []}
                  defaultStorageId={defaultStorageId}
                  onSelectionChange={(updates) => updateSelection(item.id, updates)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Off-list items section */}
        {offListItems.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              <Trans>Off-List Items</Trans> ({offListItems.length})
              <span className="ml-2 normal-case font-normal">
                — <Trans>select to add</Trans>
              </span>
            </p>
            <div className="space-y-2">
              {offListItems.map((item) => (
                <CompletionItemRow
                  key={item.id}
                  item={item}
                  selection={getSelection(item.id, false)}
                  storageLocations={storageLocations ?? []}
                  defaultStorageId={defaultStorageId}
                  onSelectionChange={(updates) => updateSelection(item.id, updates)}
                />
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <p className="text-sm text-muted-foreground text-center">
            <Trans>Adding {selectedCount} items to inventory</Trans>
          </p>
          <Button
            className="w-full rounded-lg py-3"
            onClick={() => void handleComplete()}
            disabled={completeTrip.isPending || selectedCount === 0}
          >
            {completeTrip.isPending ? (
              <Trans context="action_button">Completing...</Trans>
            ) : (
              <Trans context="action_button">Complete Trip</Trans>
            )}
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => onOpenChange(false)}>
            <Trans context="action_button">Cancel — keep shopping</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: per-item row with inline controls
// ---------------------------------------------------------------------------

function CompletionItemRow({
  item,
  selection,
  storageLocations,
  defaultStorageId,
  onSelectionChange,
}: {
  item: ShoppingTripItem;
  selection: TripItemSelection;
  storageLocations: StorageLocation[];
  defaultStorageId: string;
  onSelectionChange: (updates: Partial<TripItemSelection>) => void;
}) {
  const effectiveStorageId = selection.storageLocationId || defaultStorageId;

  return (
    <div className="border rounded-md p-3 space-y-2">
      {/* Checkbox + product name */}
      <div className="flex items-center gap-3">
        <Checkbox
          checked={selection.selected}
          onCheckedChange={(checked) => onSelectionChange({ selected: !!checked })}
        />
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted shrink-0">
            <PackageIcon className="h-4 w-4 text-muted-foreground" />
          </div>
          <span className="font-medium text-sm truncate">{item.productName ?? 'Unknown item'}</span>
        </div>
      </div>

      {/* Inline controls — only visible when selected */}
      {selection.selected && (
        <div className="flex flex-wrap gap-2 pl-9">
          {/* Storage location */}
          <select
            value={effectiveStorageId}
            onChange={(e) => onSelectionChange({ storageLocationId: e.target.value })}
            className="h-8 w-36 rounded-md border border-input bg-background px-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="">Storage</option>
            {storageLocations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </select>

          {/* Expiry presets */}
          <div className="flex gap-1 flex-wrap">
            {EXPIRY_PRESETS.map((preset) => (
              <button
                key={preset.days}
                type="button"
                onClick={() =>
                  onSelectionChange({
                    expiryDays: selection.expiryDays === preset.days ? null : preset.days,
                  })
                }
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  selection.expiryDays === preset.days
                    ? 'bg-primary/10 border-primary text-primary'
                    : 'border-input text-muted-foreground hover:border-primary/50'
                }`}
              >
                <CalendarIcon className="h-3 w-3 inline mr-1" />
                {preset.label}
              </button>
            ))}
          </div>

          {/* Quantity */}
          <div className="flex items-center gap-1">
            <Label className="text-xs text-muted-foreground">Qty:</Label>
            <Input
              type="number"
              min={1}
              value={selection.quantity}
              onChange={(e) =>
                onSelectionChange({ quantity: Math.max(1, Number(e.target.value) || 1) })
              }
              className="h-8 w-16 text-xs"
            />
          </div>
        </div>
      )}
    </div>
  );
}
